import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Package,
  Users,
  CheckSquare,
  Settings,
  BarChart3,
  Truck,
  X,
  Shield,
  MapPin,
  Target,
  Building,
  Bell,
  ChevronDown,
  ChevronRight,
  Edit
} from 'lucide-react';

const Sidebar = ({ collapsed, mobileOpen, onCloseMobile, userRole }) => {
  const location = useLocation();
  const [expandedMenus, setExpandedMenus] = React.useState({});

  // Menu items based on role (Dashboard.md requirements)
  const getMenuItems = (role) => {
    const baseItems = [
      {
        name: 'Dashboard',
        icon: LayoutDashboard,
        path: '/dashboard',
        roles: ['Administrador', 'Transportista', 'Cliente', 'Notificado']
      },
    ];

    const roleSpecificItems = {
      'Administrador': [
        {
          name: 'Usuarios',
          icon: Users,
          path: '/dashboard/admin/users',
          roles: ['Administrador']
        },
        {
          name: 'Nuevo envío',
          icon: Package,
          path: '/dashboard/nuevo-envio',
          roles: ['Administrador']
        },
        {
          name: 'Editar Manifiesto',
          icon: Edit,
          path: '/dashboard/editar-manifiesto',
          roles: ['Administrador']
        },
        {
          name: 'Actualizar Estados',
          icon: CheckSquare,
          path: '/dashboard/actualizar-estados',
          roles: ['Administrador']
        },
        {
          name: 'Administración',
          icon: Settings,
          roles: ['Administrador'],
          isGroup: true,
          children: [
            {
              name: 'Roles',
              icon: Shield,
              path: '/dashboard/admin/roles',
              roles: ['Administrador']
            },
            {
              name: 'Orígenes',
              icon: MapPin,
              path: '/dashboard/admin/origins',
              roles: ['Administrador']
            },
            {
              name: 'Destinos',
              icon: Target,
              path: '/dashboard/admin/destinations',
              roles: ['Administrador']
            },
            {
              name: 'Proveedores',
              icon: Building,
              path: '/dashboard/admin/suppliers',
              roles: ['Administrador']
            },
            {
              name: 'Notificaciones por Defecto',
              icon: Bell,
              path: '/dashboard/admin/default-notifications',
              roles: ['Administrador']
            }
          ]
        }
      ],
      'Transportista': [
        {
          name: 'Nuevo envío',
          icon: Truck,
          path: '/dashboard/nuevo-envio',
          roles: ['Transportista']
        },
        {
          name: 'Editar Manifiesto',
          icon: Edit,
          path: '/dashboard/editar-manifiesto',
          roles: ['Transportista']
        },
        {
          name: 'Actualizar Estados',
          icon: CheckSquare,
          path: '/dashboard/actualizar-estados',
          roles: ['Transportista']
        }
      ],
      'Cliente': [],
      'Notificado': []
    };

    return [...baseItems, ...(roleSpecificItems[role] || [])];
  };

  const menuItems = getMenuItems(userRole);

  const toggleMenu = (menuName) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuName]: !prev[menuName]
    }));
  };

  const MenuItem = ({ item, isCollapsed, level = 0 }) => {
    const Icon = item.icon;
    const isActive = location.pathname === item.path;
    const isExpanded = expandedMenus[item.name];
    const hasChildren = item.children && item.children.length > 0;
    const isChildActive = hasChildren && item.children.some(child => location.pathname === child.path);

    if (item.isGroup && hasChildren) {
      return (
        <div className={`${level > 0 ? 'ml-4' : ''}`}>
          <button
            onClick={() => toggleMenu(item.name)}
            className={`
              w-full flex items-center px-3 py-2 mx-2 rounded-lg text-sm font-medium transition-all duration-200
              ${isChildActive 
                ? 'bg-primary-50 text-primary-900' 
                : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
              }
              ${isCollapsed ? 'justify-center' : 'justify-start'}
            `}
            title={isCollapsed ? item.name : ''}
          >
            <Icon className={`h-5 w-5 ${isCollapsed ? '' : 'mr-3'} flex-shrink-0`} />
            {!isCollapsed && (
              <>
                <span className="truncate flex-1 text-left">{item.name}</span>
                {hasChildren && (
                  <div className="ml-2">
                    {isExpanded ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                  </div>
                )}
              </>
            )}
          </button>
          
          {!isCollapsed && isExpanded && hasChildren && (
            <div className="ml-4 mt-1 space-y-1">
              {item.children.map((child, index) => (
                <MenuItem key={index} item={child} isCollapsed={false} level={level + 1} />
              ))}
            </div>
          )}
        </div>
      );
    }

    return (
      <div className={`${level > 0 ? 'ml-4' : ''}`}>
        <NavLink
          to={item.path}
          onClick={onCloseMobile}
          className={`
            flex items-center px-3 py-2 mx-2 rounded-lg text-sm font-medium transition-all duration-200
            ${isActive 
              ? 'bg-primary-100 text-primary-900 border-r-2 border-primary-600' 
              : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
            }
            ${isCollapsed ? 'justify-center' : 'justify-start'}
            ${level > 0 ? 'text-xs' : ''}
          `}
          title={isCollapsed ? item.name : ''}
        >
          <Icon className={`h-5 w-5 ${isCollapsed ? '' : 'mr-3'} flex-shrink-0 ${level > 0 ? 'h-4 w-4' : ''}`} />
          {!isCollapsed && (
            <span className="truncate">{item.name}</span>
          )}
        </NavLink>
      </div>
    );
  };

  return (
    <>
      {/* Desktop Sidebar */}
      <aside className={`
        fixed left-0 top-16 bottom-0 bg-white border-r border-gray-200 transition-all duration-300 z-25
        ${collapsed ? 'w-16' : 'w-64'}
        hidden lg:block
      `}>
        <nav className="h-full py-4 overflow-y-auto flex flex-col">
          <div className="space-y-1">
            {menuItems.map((item, index) => (
              <MenuItem key={index} item={item} isCollapsed={collapsed} />
            ))}
          </div>
          
          {/* Logo at bottom - only visible when not collapsed */}
          {!collapsed && (
            <div className="mt-auto p-4 flex justify-center">
              <img 
                src="/ACTrackLogo.png" 
                alt="ACTrack Logo" 
                className="max-w-full h-auto max-h-24 object-contain"
              />
            </div>
          )}
        </nav>
      </aside>

      {/* Mobile Sidebar */}
      <aside className={`
        fixed left-0 top-16 bottom-0 bg-white border-r border-gray-200 w-64 z-30 transform transition-transform duration-300
        ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:hidden
      `}>
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Menú</h2>
          <button
            onClick={onCloseMobile}
            className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        
        <nav className="py-4 overflow-y-auto">
          <div className="space-y-1">
            {menuItems.map((item, index) => (
              <MenuItem key={index} item={item} isCollapsed={false} />
            ))}
          </div>
        </nav>
      </aside>
    </>
  );
};

export default Sidebar;